import { useState } from 'preact/hooks';

import { useLoyaltyProgramContext } from '@/context/LoyaltyProgramContext';
import { useNavigation } from '@/context/NavigationContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import { E_AppRoutes } from '@/types/Routes';
import type { T_RedeemptionResponse } from '@/types/Shopify';

interface I_UseRedemptionReturn {
  isRedeeming: boolean;
  applyRedemption: (redeemFn: () => Promise<T_RedeemptionResponse>) => Promise<void>;
}

export const useRedemption = (): I_UseRedemptionReturn => {
  const { onDiscountRedemption, getActiveCart } = usePlatformHandlers();
  const { setAppliedCodes, collectionScopedCodes } = useLoyaltyProgramContext();
  const { navigateTo } = useNavigation();
  const [isRedeeming, setIsRedeeming] = useState(false);

  const applyRedemption = async (redeemFn: () => Promise<T_RedeemptionResponse>) => {
    try {
      if (!onDiscountRedemption || !getActiveCart) return;

      setIsRedeeming(true);

      const data = await redeemFn();

      // Support both single discount and multiple discounts (coupons)
      const discountCodes: string[] = [];

      // Handle single discount (points redemption)
      if (data?.data?.shopifyDiscount?.discountCode) {
        discountCodes.push(data.data.shopifyDiscount.discountCode);
      }

      // Handle multiple discounts (coupons)
      if (data?.data?.shopifyDiscounts && Array.isArray(data.data.shopifyDiscounts)) {
        for (const discount of data.data.shopifyDiscounts) {
          if (discount.discountCode) {
            discountCodes.push(discount.discountCode);
          }
        }
      }

      if (discountCodes.length === 0) {
        console.error('No discount code returned from backend');
        return;
      }

      // Fetch the current cart to get authoritative applied codes (handles page-refresh scenarios
      // where in-memory appliedCodes state has been reset to [])
      const currentCart = await getActiveCart();
      // A collection-limited code stays even while Shopify reports it as not
      // applicable: it applies once an eligible product is added.
      const cartAppliedCodes =
        currentCart?.discount_codes
          ?.filter((c) => c.applicable || collectionScopedCodes.includes(c.code))
          .map((c) => c.code) ?? [];

      // Process all discount codes — always add, never toggle off
      let updatedCodes = [...cartAppliedCodes];

      for (const discountCode of discountCodes) {
        if (!updatedCodes.includes(discountCode)) {
          updatedCodes.push(discountCode);
        }
      }

      // Remove duplicates
      updatedCodes = Array.from(new Set(updatedCodes));

      await onDiscountRedemption({ code: updatedCodes.join(',') });

      const updatedCart = await getActiveCart();
      const validCodes = updatedCart?.discount_codes?.filter((codeObj) => codeObj.applicable) || [];

      setAppliedCodes(validCodes.map((codeObj) => codeObj.code));

      navigateTo(E_AppRoutes.REGISTERED_CUSTOMER);
    } catch (err) {
      console.error('Failed to redeem. Try again.', err);
      throw err;
    } finally {
      setIsRedeeming(false);
    }
  };

  return {
    isRedeeming,
    applyRedemption,
  };
};
